import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable, Subject } from 'rxjs';
import { HubConnectionBuilder, HubConnection } from '@microsoft/signalr';
import { NotificationsFeed, NotificationsFeedResponse } from '@core/model';
import { API_BASE_URL, NOTIFICATIONS_HISTORY_PULL_LIMIT } from '@core/constants';
import { AuthenticationService } from '@core/service/authentication.service';
import { GlobalBroadcastService, ISharedServices, Notification, ShellService } from '@mynexus/mfe-core';
import { NotificationType } from '@core/enumerations';

interface NotificationsFiltersQuery {
  createdDate?: [Date | null, Date | null];
  authorizationNumber?: string;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationsService {
  private readonly sharedServices: ISharedServices;
  private readonly broadcastService: GlobalBroadcastService;

  private connection: HubConnection;

  private notificationSet = new Set<string>();

  private readonly SET_BUFFER_LIMIT = 50;

  public readonly realTimeNotificationsSubject = new Subject();
  public readonly feedNotificationsSubject = new Subject();

  constructor(
    private readonly http: HttpClient,
    private readonly authService: AuthenticationService,
    private readonly shellService: ShellService,
    @Inject(API_BASE_URL) private apiBaseUrl: string
  ) {
    this.sharedServices = this.shellService.sharedServices;
    this.broadcastService = this.sharedServices.broadcastService;
  }

  setupConnection(): Promise<void> {
    if (this.connection) {
      return Promise.resolve();
    }

    this.connection = new HubConnectionBuilder()
      .withUrl(`${this.apiBaseUrl}/notifications`, {
        accessTokenFactory: () => this.authService.getToken()
      })
      .build();

    this.connection.on('global-notification', ({ type, instanceId, name, payload }) => {
      if (this.notificationSet.has(instanceId)) {
        return;
      }

      // Store instance ids to prevent message duplication
      this.notificationSet.add(instanceId);

      if (type === NotificationType.RealTime) {
        this.realTimeNotificationsSubject.next({ name, payload, instanceId });
      }

      if (type === NotificationType.FeedNotification) {
        this.feedNotificationsSubject.next({ name, payload, instanceId })
      }

      this.broadcastService.broadcast({ type, payload } as Notification);

      // To prevent memory leaks we should clean set once SET_BUFFER_LIMIT exceeded
      if (this.notificationSet.size >= this.SET_BUFFER_LIMIT) {
        this.notificationSet.clear();
      }
    });

    return this.connection.start();
  }

  closeConnection() {
    if (!this.connection) {
      console.info('No active SinalR connection');
      return;
    }

    this.connection.stop();
  }

  getFeedNotifications(top: number = 0, skip: number = 0, query: NotificationsFiltersQuery = {}): Observable<NotificationsFeed> {
    const q = `?$orderby=createdDate desc&$top=${top}&$skip=${skip}&$count=true`;

    const filters: string[] = [];

    if (query.createdDate) {
      const [startDate, endDate] = query.createdDate;

      const endDateEOD = new Date(endDate || startDate);
      endDateEOD.setHours(23, 59, 59);

      filters.push(`createdDate ge ${startDate.toISOString()}`)
      filters.push(`createdDate le ${endDateEOD.toISOString()}`)
    } else {
      // default to last 14 days
      const now = new Date();
      const defaultStart = new Date(now.getFullYear(), now.getMonth(), now.getDate() - NOTIFICATIONS_HISTORY_PULL_LIMIT);
      defaultStart.setHours(0, 0, 0, 0);

      filters.push(`createdDate ge ${defaultStart.toISOString()}`)
    }

    if (query.authorizationNumber) filters.push(`contains(payload,'${query.authorizationNumber}')`)

    const filter = filters.length > 0 ? `&$filter=${filters.join(' and ')}` : '';

    return this.getFeed(`"${q + filter}"`)
      .pipe(map(res => ({
        items: res.value.map(value => ({...value, payload: JSON.parse(value.payload)})),
        count: res['@odata.count']
      })));
  }

  getUnreadCount() {
    const filter = `"$count=true&$filter=isRead eq false"`

    return this.getFeed(filter).pipe(map(res => ({ count: res['@odata.count'] })));
  }

  markAsRead(ids: string[]): Observable<void> {
    return this.http.put<void>(`${this.apiBaseUrl}/api/feed-notifications/mark-as-read`, ids);
  }

  private getFeed(body: string): Observable<NotificationsFeedResponse> {
    return this.http.post<NotificationsFeedResponse>(`${this.apiBaseUrl}/api/feed-notifications/$query`,
      body, { headers: {'Content-Type': 'application/json'} }
    )
  }
}
